/**
 * Command Palette ⌘K (TASK-104, design §3.5).
 *
 * Single global instance mounted in `AppShell`. The `⌘K` / `Ctrl+K` hotkey
 * is registered via Mantine's `useHotkeys` at the shell level. Visual style
 * follows §3.5 — Mantine Spotlight is not in the project dependency graph,
 * so the palette is composed from `Modal` + `TextInput` + `ScrollArea` and
 * mirrors the same UX (search-on-top, keyboard-first list, group headers,
 * `indigo.0` active row).
 *
 * Scope: local route navigation + quick actions, plus a server-backed «Треки»
 * group (BUG-096) that queries `GET /admin/tracks?q=` so the most-visible
 * search field in the app actually finds tracks. Broader server-side fuzzy
 * search across accounts / venues / presets / selectors / promos (design §3.5
 * groups 3–7) is still owned by a follow-up task that wires `/admin/search`.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ScrollArea, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconArrowRight,
  IconMusic,
  IconSearch,
  type Icon,
} from '@tabler/icons-react';
import { useAuth } from '@/hooks/use-auth';
import { useTracksQuery } from '@/api/tracks';
import { usePresetsListQuery } from '@/api/presets';
import { useAccountsListQuery } from '@/api/accounts';
import { useSelectorsListQuery } from '@/api/selectors';
import { usePromoCodesQuery } from '@/api/promo-codes';
import {
  IconBuildingStore,
  IconPlaylist,
  IconTicket,
  IconUsers,
} from '@tabler/icons-react';
import {
  getVisibleItems,
  type CommandPaletteAudience,
  type CommandPaletteItem,
} from './command-palette-items';
import {
  buildAccountSearchParams,
  buildCatalogSearchPath,
  buildPresetSearchParams,
  buildPromoSearchParams,
  buildSectionSearchPath,
  buildSelectorSearchParams,
  buildTrackSearchParams,
} from './command-palette-search';
import classes from './CommandPalette.module.css';

export interface CommandPaletteProps {
  opened: boolean;
  onClose: () => void;
}

const SECTION_DEBOUNCE_MS = 150;
const TRACK_DEBOUNCE_MS = 300;
/** Shared debounce for the cross-entity groups (BUG-098). */
const ENTITY_DEBOUNCE_MS = 300;
const MAX_SECTION_RESULTS = 40;
const TRACKS_GROUP = 'Треки';
const PRESETS_GROUP = 'Подборки';
const ACCOUNTS_GROUP = 'Аккаунты';
const SELECTORS_GROUP = 'Селекторы';
const PROMOS_GROUP = 'Промокоды';

/**
 * A single navigable row in the palette. Sections and quick actions resolve
 * to a static `CommandPaletteItem`; track results and the «show all» link are
 * server-driven, so every row carries its own group + run() closure and the
 * keyboard cursor walks one flat list (`PaletteRow[]`).
 */
interface PaletteRow {
  key: string;
  group: string;
  label: string;
  description?: string;
  icon?: Icon;
  /** Cover thumbnail URL (track rows only). */
  coverUrl?: string | null;
  run: () => void;
}

interface GroupedRows {
  group: string;
  rows: PaletteRow[];
}

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function matches(item: CommandPaletteItem, needle: string): boolean {
  if (!needle) return true;
  const haystack = `${item.label} ${item.keywords ?? ''}`.toLocaleLowerCase();
  return haystack.includes(needle);
}

function groupRows(rows: PaletteRow[]): GroupedRows[] {
  const order: string[] = [];
  const buckets = new Map<string, PaletteRow[]>();
  for (const row of rows) {
    if (!buckets.has(row.group)) {
      buckets.set(row.group, []);
      order.push(row.group);
    }
    buckets.get(row.group)!.push(row);
  }
  return order.map((group) => ({ group, rows: buckets.get(group)! }));
}

/**
 * Map the auth user onto a palette audience. Mirrors the centralized
 * `canAccessAsStaff` predicate (apps/admin/src/lib/can-access-staff.ts): the
 * `admin` audience covers `role === 'admin'`, `role === 'super_admin'`, AND any
 * account carrying the elevated `is_super_admin` flag (BUG-097 — super_admins
 * previously fell through to `null` and saw no navigation items).
 */
export function getAudience(
  role: string | undefined,
  isSuperAdmin: boolean,
): CommandPaletteAudience | null {
  if (role === 'selector') return { role: 'selector' };
  if (role === 'admin' || role === 'super_admin' || isSuperAdmin) {
    return { role: 'admin' };
  }
  return null;
}

export function CommandPalette({ opened, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const titleId = useId();
  const listboxId = useId();
  const listRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [debouncedSection] = useDebouncedValue(query, SECTION_DEBOUNCE_MS);
  const [debouncedTrack] = useDebouncedValue(query, TRACK_DEBOUNCE_MS);
  const [debouncedEntity] = useDebouncedValue(query, ENTITY_DEBOUNCE_MS);

  const audience = getAudience(user?.role, user?.is_super_admin === true);

  const closeAndGo = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose],
  );

  const sectionRows = useMemo<PaletteRow[]>(() => {
    if (!audience) return [];
    const needle = normalize(debouncedSection);
    return getVisibleItems(audience)
      .filter((i) => matches(i, needle))
      .slice(0, MAX_SECTION_RESULTS)
      .map((item) => ({
        key: `section:${item.id}`,
        group: item.group,
        label: item.label,
        description: item.description,
        icon: item.icon,
        run: () => closeAndGo(item.path),
      }));
  }, [audience, debouncedSection, closeAndGo]);

  // Server-backed track search — only fires once the (debounced) query is
  // non-empty, so an empty palette never hits the API.
  const trackSearch = debouncedTrack.trim();
  const tracksEnabled = trackSearch.length > 0;
  const tracksQuery = useTracksQuery(
    buildTrackSearchParams(trackSearch),
    tracksEnabled,
  );

  const trackRows = useMemo<PaletteRow[]>(() => {
    if (!tracksEnabled) return [];
    const items = tracksQuery.data?.items ?? [];
    const rows: PaletteRow[] = items.map((track) => ({
      key: `track:${track.id}`,
      group: TRACKS_GROUP,
      label: track.title,
      description: track.artist ?? undefined,
      coverUrl: track.cover_url ?? null,
      run: () => closeAndGo(`/catalog/tracks/${track.id}`),
    }));
    rows.push({
      key: 'track:show-all',
      group: TRACKS_GROUP,
      label: 'Показать все результаты в Каталоге',
      icon: IconArrowRight,
      run: () => closeAndGo(buildCatalogSearchPath(trackSearch)),
    });
    return rows;
  }, [tracksEnabled, tracksQuery.data, trackSearch, closeAndGo]);

  // Cross-entity search (BUG-098). The four list endpoints are staff-only
  // (`@AdminOrAbove()` / `@AdminSelectorOrAbove()` server-side), so the groups
  // are gated to the `admin` audience — a selector never fires these queries,
  // which keeps RBAC honest and avoids 403 noise. Each query is debounced and
  // `enabled` only when the (trimmed) input is non-empty so an empty palette
  // never hits the API.
  const entitySearch = debouncedEntity.trim();
  const entitiesEnabled =
    audience?.role === 'admin' && entitySearch.length > 0;

  const presetsQuery = usePresetsListQuery(
    buildPresetSearchParams(entitySearch),
    { enabled: entitiesEnabled },
  );
  const accountsQuery = useAccountsListQuery(
    buildAccountSearchParams(entitySearch),
    { enabled: entitiesEnabled },
  );
  const selectorsQuery = useSelectorsListQuery(
    buildSelectorSearchParams(entitySearch),
    { enabled: entitiesEnabled },
  );
  const promosQuery = usePromoCodesQuery(
    buildPromoSearchParams(entitySearch),
    { enabled: entitiesEnabled },
  );

  const presetRows = useMemo<PaletteRow[]>(() => {
    if (!entitiesEnabled) return [];
    const rows: PaletteRow[] = (presetsQuery.data?.items ?? []).map((preset) => ({
      key: `preset:${preset.id}`,
      group: PRESETS_GROUP,
      label: preset.name,
      description: preset.description ?? undefined,
      icon: IconPlaylist,
      run: () => closeAndGo(`/presets/${preset.id}`),
    }));
    rows.push({
      key: 'preset:show-all',
      group: PRESETS_GROUP,
      label: 'Показать все в Подборках',
      icon: IconArrowRight,
      run: () => closeAndGo(buildSectionSearchPath('/presets', entitySearch)),
    });
    return rows;
  }, [entitiesEnabled, presetsQuery.data, entitySearch, closeAndGo]);

  const accountRows = useMemo<PaletteRow[]>(() => {
    if (!entitiesEnabled) return [];
    const rows: PaletteRow[] = (accountsQuery.data?.items ?? []).map(
      (account) => ({
        key: `account:${account.id}`,
        group: ACCOUNTS_GROUP,
        label: account.name,
        description: account.primary_contact_email || undefined,
        icon: IconBuildingStore,
        run: () => closeAndGo(`/accounts/${account.id}`),
      }),
    );
    rows.push({
      key: 'account:show-all',
      group: ACCOUNTS_GROUP,
      label: 'Показать все в Аккаунтах',
      icon: IconArrowRight,
      run: () => closeAndGo(buildSectionSearchPath('/accounts', entitySearch)),
    });
    return rows;
  }, [entitiesEnabled, accountsQuery.data, entitySearch, closeAndGo]);

  const selectorRows = useMemo<PaletteRow[]>(() => {
    if (!entitiesEnabled) return [];
    const rows: PaletteRow[] = (selectorsQuery.data?.items ?? []).map(
      (selector) => ({
        key: `selector:${selector.id}`,
        group: SELECTORS_GROUP,
        label: selector.name,
        description: selector.email || undefined,
        icon: IconUsers,
        run: () => closeAndGo(`/selectors/${selector.id}`),
      }),
    );
    rows.push({
      key: 'selector:show-all',
      group: SELECTORS_GROUP,
      label: 'Показать все в Селекторах',
      icon: IconArrowRight,
      run: () => closeAndGo(buildSectionSearchPath('/selectors', entitySearch)),
    });
    return rows;
  }, [entitiesEnabled, selectorsQuery.data, entitySearch, closeAndGo]);

  const promoRows = useMemo<PaletteRow[]>(() => {
    if (!entitiesEnabled) return [];
    const rows: PaletteRow[] = (promosQuery.data?.items ?? []).map((promo) => ({
      key: `promo:${promo.id}`,
      group: PROMOS_GROUP,
      label: promo.code,
      description: promo.notes ?? undefined,
      icon: IconTicket,
      run: () => closeAndGo(`/promos/${promo.id}`),
    }));
    rows.push({
      key: 'promo:show-all',
      group: PROMOS_GROUP,
      label: 'Показать все в Промокодах',
      icon: IconArrowRight,
      run: () => closeAndGo(buildSectionSearchPath('/promos', entitySearch)),
    });
    return rows;
  }, [entitiesEnabled, promosQuery.data, entitySearch, closeAndGo]);

  const flatRows = useMemo<PaletteRow[]>(
    () => [
      ...sectionRows,
      ...trackRows,
      ...presetRows,
      ...accountRows,
      ...selectorRows,
      ...promoRows,
    ],
    [sectionRows, trackRows, presetRows, accountRows, selectorRows, promoRows],
  );

  const groups = useMemo(() => groupRows(flatRows), [flatRows]);

  // Show the "searching…" hint while any server-backed group is still loading
  // and nothing has resolved yet — keeps the empty state from flashing
  // "Ничего не найдено" before the queries settle.
  const searching =
    (tracksEnabled && tracksQuery.isPending) ||
    (entitiesEnabled &&
      (presetsQuery.isPending ||
        accountsQuery.isPending ||
        selectorsQuery.isPending ||
        promosQuery.isPending));

  // Reset query / focus to first row whenever the palette is re-opened.
  useEffect(() => {
    if (opened) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [opened]);

  // Clamp the active cursor when the result list shrinks (e.g. typing).
  useEffect(() => {
    setActiveIndex((idx) => {
      if (flatRows.length === 0) return 0;
      if (idx >= flatRows.length) return flatRows.length - 1;
      return idx;
    });
  }, [flatRows.length]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (flatRows.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((idx) => (idx + 1) % flatRows.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((idx) => (idx - 1 + flatRows.length) % flatRows.length);
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActiveIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActiveIndex(flatRows.length - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        flatRows[activeIndex]?.run();
      }
    },
    [activeIndex, flatRows],
  );

  // Scroll the active row into view as the cursor moves.
  useEffect(() => {
    if (!opened) return;
    const container = listRef.current;
    if (!container) return;
    const activeEl = container.querySelector<HTMLElement>('[data-active="true"]');
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, opened]);

  let cursor = -1;
  const activeId = `${listboxId}-row-${activeIndex}`;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size="lg"
      padding={0}
      radius="md"
      aria-labelledby={titleId}
      classNames={{ body: classes.modalBody }}
      transitionProps={{ duration: 120 }}
    >
      <Text id={titleId} component="h2" hidden>
        Командная палитра
      </Text>

      <TextInput
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        placeholder="Поиск… (⌘K)"
        size="md"
        leftSection={<IconSearch size={18} stroke={1.5} />}
        variant="unstyled"
        className={classes.search}
        aria-label="Поиск в командной палитре"
        aria-controls={listboxId}
        aria-activedescendant={flatRows.length > 0 ? activeId : undefined}
        role="combobox"
        aria-expanded={flatRows.length > 0}
        aria-autocomplete="list"
      />

      <ScrollArea
        className={classes.results}
        type="auto"
        viewportRef={listRef}
      >
        {flatRows.length === 0 ? (
          <div className={classes.empty} role="status">
            <div className={classes.emptyTitle}>
              {searching ? 'Идёт поиск…' : 'Ничего не найдено'}
            </div>
            {!searching ? (
              <div className={classes.emptyHint}>
                Начните вводить запрос — поиск ищет по разделам админки, трекам,
                подборкам, аккаунтам, селекторам и промокодам.
              </div>
            ) : null}
          </div>
        ) : (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Результаты поиска"
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {groups.map((group) => (
              <li key={group.group}>
                <div className={classes.groupHeader} aria-hidden>
                  {group.group}
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {group.rows.map((row) => {
                    cursor += 1;
                    const flatIndex = cursor;
                    const isActive = flatIndex === activeIndex;
                    const rowId = `${listboxId}-row-${flatIndex}`;
                    const Icon = row.icon;
                    return (
                      <li key={row.key} role="presentation">
                        <button
                          type="button"
                          id={rowId}
                          role="option"
                          aria-selected={isActive}
                          data-active={isActive}
                          className={classes.row}
                          onClick={() => row.run()}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                        >
                          {row.coverUrl ? (
                            <img
                              src={row.coverUrl}
                              alt=""
                              className={classes.cover}
                              aria-hidden
                            />
                          ) : Icon ? (
                            <Icon
                              size={18}
                              stroke={1.5}
                              className={classes.rowIcon}
                              aria-hidden
                            />
                          ) : (
                            <IconMusic
                              size={18}
                              stroke={1.5}
                              className={classes.rowIcon}
                              aria-hidden
                            />
                          )}
                          <span className={classes.rowLabel}>
                            <span>{row.label}</span>
                            {row.description ? (
                              <span className={classes.rowDescription}>
                                {row.description}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>

      <div className={classes.footer} aria-hidden>
        <span>
          <span className={classes.kbd}>↑</span>{' '}
          <span className={classes.kbd}>↓</span> навигация
        </span>
        <span>
          <span className={classes.kbd}>Enter</span> открыть
        </span>
        <span>
          <span className={classes.kbd}>Esc</span> закрыть
        </span>
      </div>
    </Modal>
  );
}
